import { useState } from "react";
import { AttendanceList } from "./AttendanceList";
import { AttendanceFilters } from "./AttendanceFilters";
import { AttendanceStats } from "./AttendanceStats";
import { UserAttendanceModal } from "./UserAttendanceModal";
import { useAttendanceLogs, AttendanceLog, useAttendanceRealtime } from "@/hooks/admin/attendance";

interface AttendanceTabProps {
  gymId: string;
}

export const AttendanceTab = ({ gymId }: AttendanceTabProps) => {
  const [page, setPage] = useState(1);
  // Initialize with today's date by default
  const today = new Date();
  const [dateFrom, setDateFrom] = useState<Date | undefined>(today);
  const [dateTo, setDateTo] = useState<Date | undefined>(today);
  const [selectedUser, setSelectedUser] = useState<AttendanceLog["user"] | null>(null);

  // Enable real-time updates for this gym
  useAttendanceRealtime({ gymId, enabled: !!gymId });

  const { data, isLoading } = useAttendanceLogs({
    gymId,
    page,
    pageSize: 20,
    dateFrom,
    dateTo,
  });

  const handleUserClick = (user: AttendanceLog["user"]) => {
    if (user) {
      setSelectedUser(user);
    }
  };

  const handleCloseModal = () => {
    setSelectedUser(null);
  };

  const handleFilterChange = (from: Date | undefined, to: Date | undefined) => {
    setDateFrom(from);
    setDateTo(to);
    setPage(1); // Reset to first page when filters change
  };

  const handleTodayClick = () => {
    const today = new Date();
    setDateFrom(today);
    setDateTo(today);
    setPage(1);
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Stats Dashboard - now synced with filters */}
      <AttendanceStats gymId={gymId} dateFrom={dateFrom} dateTo={dateTo} />

      {/* Filters */}
      <AttendanceFilters
        dateFrom={dateFrom}
        dateTo={dateTo}
        onFilterChange={handleFilterChange}
        onTodayClick={handleTodayClick}
      />
      
      {/* Attendance List */}
      <AttendanceList
        logs={data?.logs || []}
        isLoading={isLoading}
        page={page}
        totalPages={data?.totalPages || 1}
        onPageChange={setPage}
        onUserClick={handleUserClick}
      />

      {selectedUser && (
        <UserAttendanceModal
          user={selectedUser}
          gymId={gymId}
          isOpen={!!selectedUser}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};
